package link.erpango.quip.db.composeApp

import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass
import link.erpango.quip.db.QuipDb
import link.erpango.quip.db.SparePartDocumentQueries
import link.erpango.quip.db.VehicleDocumentQueries
import linkerpangoquipdb.AuthQueries
import linkerpangoquipdb.CommonFlagsQueries
import linkerpangoquipdb.CommonValuesQueries
import linkerpangoquipdb.GarageQueries
import linkerpangoquipdb.MaintenanceQueries
import linkerpangoquipdb.MaintenanceSparePartsQueries
import linkerpangoquipdb.SparePartQueries
import linkerpangoquipdb.UserQueries
import linkerpangoquipdb.Vehicle
import linkerpangoquipdb.VehicleCatalogQueries

internal val KClass<QuipDb>.schema: SqlSchema<QueryResult.AsyncValue<Unit>>
  get() = QuipDbImpl.Schema

internal fun KClass<QuipDb>.newInstance(driver: SqlDriver, vehicleAdapter: Vehicle.Adapter): QuipDb = QuipDbImpl(driver, vehicleAdapter)

private class QuipDbImpl(
  driver: SqlDriver,
  vehicleAdapter: Vehicle.Adapter,
) : SuspendingTransacterImpl(driver),
    QuipDb {
  override val authQueries: AuthQueries = AuthQueries(driver)

  override val commonFlagsQueries: CommonFlagsQueries = CommonFlagsQueries(driver)

  override val commonValuesQueries: CommonValuesQueries = CommonValuesQueries(driver)

  override val garageQueries: GarageQueries = GarageQueries(driver, vehicleAdapter)

  override val maintenanceQueries: MaintenanceQueries = MaintenanceQueries(driver)

  override val maintenanceSparePartsQueries: MaintenanceSparePartsQueries =
      MaintenanceSparePartsQueries(driver)

  override val sparePartQueries: SparePartQueries = SparePartQueries(driver)

  override val sparePartDocumentQueries: SparePartDocumentQueries = SparePartDocumentQueries(driver)

  override val userQueries: UserQueries = UserQueries(driver)

  override val vehicleCatalogQueries: VehicleCatalogQueries = VehicleCatalogQueries(driver)

  override val vehicleDocumentQueries: VehicleDocumentQueries = VehicleDocumentQueries(driver)

  public object Schema : SqlSchema<QueryResult.AsyncValue<Unit>> {
    override val version: Long
      get() = 6

    override fun create(driver: SqlDriver): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      driver.execute(null, """
          |CREATE TABLE auth_table (
          |    user_id TEXT,
          |    session_token TEXT,
          |    refresh_token TEXT,
          |    created_at INTEGER,
          |    PRIMARY KEY (user_id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE common_flags (
          |    id TEXT PRIMARY KEY NOT NULL ON CONFLICT REPLACE,
          |    value INTEGER DEFAULT 0 NOT NULL,
          |    last_update INTEGER NOT NULL
          |    )
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE common_values (
          |    id TEXT PRIMARY KEY NOT NULL ON CONFLICT REPLACE,
          |    value TEXT NOT NULL,
          |    last_update INTEGER NOT NULL
          |    )
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE vehicle (
          |    vin TEXT PRIMARY KEY NOT NULL,
          |    plate TEXT NOT NULL,
          |    brand TEXT NOT NULL,
          |    model TEXT NOT NULL,
          |    vehicle_type TEXT NOT NULL DEFAULT 'car',
          |    year INTEGER NOT NULL,
          |    color TEXT NOT NULL,
          |    owner TEXT NOT NULL,
          |    engine_oil TEXT NOT NULL,
          |    front_tire_size TEXT NOT NULL,
          |    rear_tire_size TEXT NOT NULL,
          |    front_tire_pressure_below_160 REAL NOT NULL,
          |    rear_tire_pressure_below_160 REAL NOT NULL,
          |    front_tire_pressure_above_160 REAL NOT NULL,
          |    rear_tire_pressure_above_160 REAL NOT NULL,
          |    recommended_fuel TEXT NOT NULL,
          |    engine_details TEXT NOT NULL,
          |    cylinder_count INTEGER NOT NULL,
          |    front_bulb_type TEXT NOT NULL DEFAULT '',
          |    rear_bulb_type TEXT NOT NULL DEFAULT '',
          |    battery_specs TEXT NOT NULL DEFAULT '',
          |    coolant_type TEXT NOT NULL DEFAULT '',
          |    signature TEXT DEFAULT NULL,
          |    next_revision_date INTEGER,
          |    next_itv_date INTEGER,
          |    first_registration_date INTEGER
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE maintenance (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    server_id INTEGER UNIQUE,
          |    vehicle_vin TEXT NOT NULL,
          |    kilometers INTEGER NOT NULL,
          |    mechanic TEXT NOT NULL,
          |    workshop TEXT NOT NULL,
          |    description TEXT NOT NULL,
          |    date INTEGER NOT NULL,
          |    cost REAL NOT NULL,
          |    signature TEXT DEFAULT NULL,
          |    FOREIGN KEY (vehicle_vin) REFERENCES vehicle(vin) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE maintenance_spare_parts (
          |    maintenance_id INTEGER NOT NULL,
          |    spare_part_id INTEGER NOT NULL,
          |    FOREIGN KEY(maintenance_id) REFERENCES maintenance(id),
          |    FOREIGN KEY(spare_part_id) REFERENCES spare_part(id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE spare_part (
          |    id INTEGER PRIMARY KEY AUTOINCREMENT,
          |    server_id INTEGER UNIQUE,
          |    category_id TEXT NOT NULL,
          |    spare_part_id TEXT NOT NULL,
          |    brand TEXT NOT NULL,
          |    model TEXT NOT NULL,
          |    additional_info TEXT NOT NULL,
          |    durability_days INTEGER,
          |    quantity REAL DEFAULT 1.0,
          |    unit TEXT,
          |    signature TEXT DEFAULT NULL,
          |    reference TEXT
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE SparePartDocument (
          |    id INTEGER NOT NULL PRIMARY KEY AUTOINCREMENT,
          |    server_id INTEGER UNIQUE,
          |    spare_part_id INTEGER NOT NULL, -- Local PK of Spare Part
          |    server_spare_part_id INTEGER,   -- Backend ID of Spare Part
          |    vehicle_vin TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    file_path TEXT NOT NULL,  -- Local file path
          |    file_type TEXT NOT NULL,
          |    created_at TEXT NOT NULL,
          |    size_mb REAL NOT NULL DEFAULT 0.0,
          |    updated_at TEXT,
          |    FOREIGN KEY(spare_part_id) REFERENCES spare_part(id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE user_table (
          |    id TEXT,
          |    email TEXT,
          |    name TEXT,
          |    avatar TEXT,
          |    updated_at INTEGER,
          |    PRIMARY KEY (id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE vehicle_brand (
          |    id INTEGER PRIMARY KEY NOT NULL,
          |    name TEXT NOT NULL,
          |    updated_at INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE vehicle_model (
          |    id INTEGER PRIMARY KEY NOT NULL,
          |    brand_id INTEGER NOT NULL,
          |    name TEXT NOT NULL,
          |    updated_at INTEGER NOT NULL,
          |    FOREIGN KEY (brand_id) REFERENCES vehicle_brand(id)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE VehicleDocument (
          |    id INTEGER NOT NULL PRIMARY KEY AUTOINCREMENT,
          |    server_id INTEGER UNIQUE,
          |    vehicle_vin TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    file_path TEXT NOT NULL,  -- Local file path
          |    file_type TEXT NOT NULL,
          |    created_at TEXT NOT NULL,
          |    size_mb REAL NOT NULL DEFAULT 0.0,
          |    updated_at TEXT
          |)
          """.trimMargin(), 0).await()
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      if (oldVersion <= 2 && newVersion > 2) {
        driver.execute(null, "ALTER TABLE user_table RENAME TO user_table_old", 0).await()
        driver.execute(null, """
            |CREATE TABLE user_table (
            |    id TEXT,
            |    email TEXT,
            |    name TEXT,
            |    avatar TEXT,
            |    updated_at INTEGER,
            |    PRIMARY KEY (id)
            |)
            """.trimMargin(), 0).await()
        driver.execute(null, "INSERT INTO user_table (id, email, name, avatar, updated_at) SELECT id, email, name, avatar, updated_at FROM user_table_old", 0).await()
        driver.execute(null, "DROP TABLE user_table_old", 0).await()
      }
      if (oldVersion <= 3 && newVersion > 3) {
        driver.execute(null, "DROP TABLE IF EXISTS spare_part", 0).await()
        driver.execute(null, """
            |CREATE TABLE spare_part (
            |    id INTEGER PRIMARY KEY AUTOINCREMENT,
            |    category_id TEXT NOT NULL,
            |    spare_part_id TEXT NOT NULL,
            |    brand TEXT NOT NULL,
            |    model TEXT NOT NULL,
            |    additional_info TEXT NOT NULL,
            |    durability_days INTEGER,
            |    quantity REAL,
            |    unit TEXT
            |)
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 4 && newVersion > 4) {
        driver.execute(null, """
            |CREATE TABLE vehicle_brand (
            |    id INTEGER PRIMARY KEY NOT NULL,
            |    name TEXT NOT NULL,
            |    updated_at INTEGER NOT NULL
            |)
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |CREATE TABLE vehicle_model (
            |    id INTEGER PRIMARY KEY NOT NULL,
            |    brand_id INTEGER NOT NULL,
            |    name TEXT NOT NULL,
            |    updated_at INTEGER NOT NULL,
            |    FOREIGN KEY (brand_id) REFERENCES vehicle_brand(id)
            |)
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 5 && newVersion > 5) {
        driver.execute(null, "ALTER TABLE vehicle ADD COLUMN front_bulb_type TEXT NOT NULL DEFAULT ''", 0).await()
        driver.execute(null, "ALTER TABLE vehicle ADD COLUMN rear_bulb_type TEXT NOT NULL DEFAULT ''", 0).await()
        driver.execute(null, "ALTER TABLE vehicle ADD COLUMN battery_specs TEXT NOT NULL DEFAULT ''", 0).await()
        driver.execute(null, "ALTER TABLE vehicle ADD COLUMN coolant_type TEXT NOT NULL DEFAULT ''", 0).await()
      }
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion + 1).await()
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion).await()
      }
    }
  }
}
